// other button (Business UI handoff). One dominant action per screen.
//   primary    — ink (black) + white. The screen's main action.
//   destructive— historically the merch'd "rust CTA" (send / approve); it is a
//                primary action, so it is ink too. Truly destructive actions
//                should say so in words and confirm.
//   outline    — white + rule border. Secondary actions (the handoff default).
//   ghost      — transparent. Tertiary / icon buttons.
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-[#63768b]",
  {
    variants: {
      variant: {
        primary:
          "bg-ink text-white hover:bg-ink-700 active:bg-ink-800",
        destructive:
          "bg-ink text-white hover:bg-ink-700 active:bg-ink-800",
        // A truly destructive action (delete, void): red, and it says so.
        risk:
          "bg-risk text-white hover:bg-[#7E2F28] active:bg-[#66251F]",
        ghost:
          "bg-transparent text-ink hover:bg-cream-100 active:bg-cream-200",
        outline:
          "border border-rule bg-white text-ink hover:bg-cream-100",
      },
      size: {
        sm: "h-8 px-3 text-sm",
        md: "h-10 px-[14px] text-sm",
        lg: "h-12 px-6 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";
